import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mail, Volume2, VolumeX } from "lucide-react";

const YOUTUBE_ID = "mmuYPhdq35g";

// Load the YouTube IFrame API once.
function loadYouTubeAPI() {
  if (window.YT && window.YT.Player) return Promise.resolve();
  if (window._ytApiPromise) return window._ytApiPromise;
  window._ytApiPromise = new Promise((resolve) => {
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    document.head.appendChild(tag);
    window.onYouTubeIframeAPIReady = () => resolve();
  });
  return window._ytApiPromise;
}

export default function VSLVideoPage() {
  const playerContainerRef = useRef(null);
  const playerRef = useRef(null);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    document.title = "Watch The Funnel — BrandChilly";
    try {
      if (window.fbq) window.fbq("track", "ViewContent", { content_name: "VSLVideo", content_category: "VSL Funnel" });
      if (window.gtag) window.gtag("event", "view_content", { content_name: "VSLVideo" });
    } catch (e) { /* swallow */ }

    // Stamp the source so /book picks it up on the next form submit.
    try {
      sessionStorage.setItem("bc_booking_source", "VSL");
    } catch (e) { /* swallow */ }

    let cancelled = false;
    loadYouTubeAPI().then(() => {
      if (cancelled || !playerContainerRef.current) return;
      playerRef.current = new window.YT.Player(playerContainerRef.current, {
        videoId: YOUTUBE_ID,
        playerVars: {
          autoplay: 1,
          mute: 1,
          controls: 0,
          rel: 0,
          modestbranding: 1,
          playsinline: 1,
          fs: 0,
          disablekb: 1,
          iv_load_policy: 3,
        },
        events: {
          onReady: (ev) => {
            try {
              ev.target.mute();
              ev.target.playVideo();
            } catch (e) { /* swallow */ }
          },
        },
      });
    });

    return () => {
      cancelled = true;
      try { playerRef.current && playerRef.current.destroy(); } catch (e) { /* swallow */ }
    };
  }, []);

  const toggleMute = () => {
    const p = playerRef.current;
    if (!p) return;
    try {
      if (muted) {
        p.unMute();
        p.setVolume(80);
        setMuted(false);
      } else {
        p.mute();
        setMuted(true);
      }
    } catch (e) { /* swallow */ }
  };

  const onBookClick = () => {
    try {
      sessionStorage.setItem("bc_booking_source", "VSL");
      if (window.fbq) window.fbq("track", "Lead", { content_name: "VSLVideo", source: "vsl_book_cta" });
      if (window.gtag) window.gtag("event", "generate_lead", { value: 1, currency: "INR", source: "vsl_book_cta" });
    } catch (e) { /* swallow */ }
  };

  return (
    <div className="coaches-page" style={{ minHeight: "100vh", background: "var(--c-bg)" }}>
      {/* Big Logo Header */}
      <header className="border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 md:py-12 flex items-center justify-center">
          <img
            src="/assets/logo.png"
            alt="BrandChilly"
            className="h-24 md:h-36 w-auto"
            data-testid="vsl-video-logo"
          />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 md:px-8 py-10 md:py-16 text-center">
        {/* Headline */}
        <h1
          className="text-3xl sm:text-5xl md:text-6xl leading-[1.05] text-white"
          style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 600 }}
          data-testid="vsl-video-headline"
        >
          50 High-Ticket Clients. <span className="c-red italic">One System.</span>
        </h1>

        {/* Subheadline */}
        <p
          className="mt-6 md:mt-7 max-w-2xl mx-auto text-base md:text-lg c-muted leading-relaxed"
          data-testid="vsl-video-subheadline"
        >
          Watch the breakdown below to see the exact marketing funnel coaches, freelancers and
          agencies are using to enrol premium clients — without cold DMs, webinars, or burning
          out on content.
        </p>

        {/* Video — autoplay muted, custom mute/unmute control */}
        <div className="mt-10 md:mt-14 mx-auto" style={{ maxWidth: "880px" }} data-testid="vsl-video-wrapper">
          <div
            style={{
              position: "relative",
              paddingBottom: "56.25%",
              height: 0,
              overflow: "hidden",
              background: "#000",
              border: "1px solid #1f1f1f",
            }}
          >
            <div
              ref={playerContainerRef}
              style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
              data-testid="vsl-youtube-player"
            />

            {/* Click-to-unmute overlay (only visible while muted) — boosts the chance the user actually turns sound on */}
            {muted && (
              <button
                type="button"
                onClick={toggleMute}
                aria-label="Unmute video"
                data-testid="vsl-tap-to-unmute"
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  background: "rgba(0,0,0,0.0)",
                  border: 0,
                  cursor: "pointer",
                  zIndex: 1,
                }}
              >
                <span
                  style={{
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    transform: "translate(-50%, -50%)",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "10px",
                    background: "rgba(0,0,0,0.7)",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.2)",
                    padding: "12px 22px",
                    fontSize: "14px",
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                  }}
                >
                  <VolumeX size={16} /> Tap to unmute
                </span>
              </button>
            )}

            {/* Persistent mute/unmute button at bottom-right */}
            <button
              type="button"
              onClick={toggleMute}
              aria-label={muted ? "Unmute video" : "Mute video"}
              data-testid="vsl-mute-toggle"
              style={{
                position: "absolute",
                right: "14px",
                bottom: "14px",
                width: "44px",
                height: "44px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(0,0,0,0.65)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.15)",
                cursor: "pointer",
                zIndex: 2,
              }}
            >
              {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 md:mt-16">
          <Link
            to="/book"
            onClick={onBookClick}
            className="c-btn-primary text-base md:text-lg px-8 md:px-10 py-4 md:py-5 inline-flex"
            data-testid="vsl-book-cta"
          >
            Book a Discovery Call <ArrowRight size={20} />
          </Link>
        </div>

        {/* Supporting text */}
        <div className="mt-10 md:mt-14 max-w-xl mx-auto space-y-3 text-sm md:text-base c-muted leading-relaxed">
          <p>Book your consultation call and fill out the application in detail.</p>
          <p>We'll review your application and speak with you to see if you're a good fit.</p>
          <p className="text-white" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            <span className="c-red">Only a few slots left.</span>
          </p>
          <p className="pt-2">
            Any questions?{" "}
            <a
              href="mailto:brandchilly@gmail.com"
              className="text-white hover:c-red transition inline-flex items-center gap-1.5"
              data-testid="vsl-contact-email"
            >
              <Mail size={14} className="c-red" /> brandchilly@gmail.com
            </a>
          </p>
        </div>
      </main>

      <footer className="border-t border-white/5 py-8 mt-10">
        <div className="max-w-6xl mx-auto px-5 md:px-8 text-center text-[11px] text-zinc-600 tracking-[0.18em] uppercase">
          BrandChilly &middot; Personal Branding &amp; Business Growth &middot; India
        </div>
      </footer>
    </div>
  );
}
